import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { environment } from '@env/environment';
import { ApiService } from '@services/api.service';

import { CurrencyModel, CurrencyRateModel } from '../models/common';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { map, switchMap, tap } from 'rxjs/operators';
import { INDEXED_DB_CONFIG } from '@app/app-indexed-db.const';
import { IndexedDbService } from '@shared/indexed-db/services';

@Injectable({
  providedIn: 'root',
})
export class CurrenciesRepository {
  constructor(private apiService: ApiService, private iDbService: IndexedDbService) {}

  public getCurrencies(): Observable<CurrencyModel[]> {
    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.CURRENCIES).pipe(
      switchMap((currencies: CurrencyModel[]) => {
        if (!!currencies?.length) {
          return of(currencies);
        } else {
          return this.apiService.get<CurrencyModel[]>(`${environment.url.prefixCustoms}v2/currencies`, null, {
            isShowLoader: false,
          });
        }
      }),
    );
  }

  public getRate(code: string, effectiveDate: string): Observable<CurrencyRateModel> {
    return !!code
      ? this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.INCOTERMS).pipe(
          map((currencies: CurrencyRateModel[]) =>
            currencies.find((item: CurrencyRateModel) => item.code === code && item.date === effectiveDate),
          ),
          switchMap((currency: CurrencyRateModel) => {
            return !!currency
              ? of(currency)
              : this.apiService
                  .get<CurrencyRateModel>(
                    `${environment.url.prefixCustoms}v2/currencies/${code}`,
                    {
                      params: {
                        effectiveDate: effectiveDate,
                      },
                    },
                    { isUsed: true },
                  )
                  .pipe(
                    tap((currencyFromAPI: CurrencyRateModel) =>
                      this.iDbService.addOrUpdateOne(INDEXED_DB_CONFIG.TABLES.CURRENCIES_BY_DATE, {
                        id: `${code}-${effectiveDate}`,
                        ...currencyFromAPI,
                      }),
                    ),
                  );
          }),
        )
      : of({ code: DEFAULT_CURRENCY_CODE, name: null, date: '0001-01-01T00:00:00+00:00', rate: 1.0 }); // fallback for broken calls
  }
}
